import React, { useState, useContext, useEffect } from "react";
import {
  Typography,
  Avatar,
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { AccountCircle } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AuthContext } from "../context/AuthContext";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import getUser from "../api/app/getUser";
import ChangePasswordDialog from "../component/ChangePasswordDialog";

const ProfilePage = () => {
  const userContext = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const [openChangePassword, setOpenChangePassword] = useState(false);

  const handleChangePasswordClose = () => {
    setOpenChangePassword(false);
  };

  const handleChangePasswordOpen = () => {
    setOpenChangePassword(true);
  };

  const handleBackButtonClick = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const responseData = await getUser();
        if (responseData.code !== null) {
          setUser(responseData);
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error("Failed to fetch user data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100vw",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: theme.palette.background.default,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        background: theme.palette.background.default,
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ minHeight: 56, px: 2 }}>
          <IconButton
            edge="start"
            aria-label="back"
            onClick={handleBackButtonClick}
            sx={{ color: "info.main" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            個人資料
          </Typography>
          <Box sx={{ width: 40 }} />
        </Toolbar>
      </AppBar>

      <Box maxWidth={400} mx="auto" mt={4} p={3}>
        {user ? (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Avatar sx={{ width: 80, height: 80, mb: 2 }}>
              <AccountCircle sx={{ width: 80, height: 80 }} />
            </Avatar>

            <Typography variant="h5" gutterBottom fontWeight="bold" color="text.primary">
              {user?.chName || "未設定名稱"}
            </Typography>

            <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mt: 2 }}>
              <ListItem>
                <ListItemText
                  primary="手機號碼"
                  secondary={user.phone || "未設定"}
                  primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                  secondaryTypographyProps={{ color: 'text.primary', variant: 'body1', fontWeight: 500 }}
                />
              </ListItem>
              <Divider component="li" />
              <ListItem>
                <ListItemText
                  primary="使用者類型"
                  secondary={user.appUserType === "guest" ? "訪客" : user.appUserType}
                  primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                  secondaryTypographyProps={{ color: 'text.primary', variant: 'body1', fontWeight: 500 }}
                />
              </ListItem>
              <Divider component="li" />
              <ListItem>
                <ListItemText
                  primary="帳號"
                  secondary={user.account || "未設定"}
                  primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                  secondaryTypographyProps={{ color: 'text.primary', variant: 'body1', fontWeight: 500 }}
                />
              </ListItem>
              <Divider component="li" />
              <ListItem
                secondaryAction={
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={handleChangePasswordOpen}
                  >
                    變更密碼
                  </Button>
                }
              >
                <ListItemText
                  primary="密碼"
                  secondary="********"
                  primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                  secondaryTypographyProps={{ color: 'text.primary', variant: 'body1', fontWeight: 500 }}
                />
              </ListItem>
            </List>
          </Box>
        ) : (
          <Box mt={4} display="flex" justifyContent="center">
            <Typography variant="h6" color="text.secondary">
              無法載入使用者資料
            </Typography>
          </Box>
        )}
      </Box>

      <ChangePasswordDialog
        handleChangePasswordClose={handleChangePasswordClose}
        open={openChangePassword}
      />
    </Box>
  );
};

export default ProfilePage;
